/**
 * Generate Upload Link Modal Component
 * TASK-361: Document Management & Compliance System
 *
 * Modal for creating secure upload links that allow clients to upload
 * documents through a public portal with Email OTP verification.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Link2,
  Mail,
  FileText,
  Info,
  Copy,
  Check,
  Loader2,
  Search,
  User,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DocType,
  CreateSecureUploadLinkRequest,
  getDocTypeLabel,
} from '../../types/documentCompliance';
import { secureUploadLinkService } from '../../services/secureUploadLinkService';
import { clientService, Client } from '../../services/clientService';

interface GenerateUploadLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId?: string;  // Now optional - if not provided, show client selector
  clientName?: string;
  clientEmail?: string;
  onSuccess?: (portalUrl: string) => void;
}

// Available document types for upload
const AVAILABLE_DOC_TYPES: DocType[] = [
  'kyc',
  'portfolio',
  'risk-profile',
  'aml',
  'compliance',
  'contracts',
  'general',
];

// Expiration options in days
const EXPIRATION_OPTIONS = [
  { value: 3, label: '3 days' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
];

const GenerateUploadLinkModal: React.FC<GenerateUploadLinkModalProps> = ({
  isOpen,
  onClose,
  clientId: initialClientId,
  clientName: initialClientName,
  clientEmail: initialClientEmail,
  onSuccess,
}) => {
  // Client selection state (when no clientId provided)
  const [clients, setClients] = useState<Client[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Effective client values (from props or selection)
  const clientId = initialClientId || selectedClient?.id || '';
  const clientName = initialClientName || selectedClient?.name;
  const clientEmail = initialClientEmail || selectedClient?.email;

  // Form state
  const [selectedDocTypes, setSelectedDocTypes] = useState<DocType[]>(['general']);
  const [messageToClient, setMessageToClient] = useState('');
  const [maxUploads, setMaxUploads] = useState(5);
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [sendEmail, setSendEmail] = useState(true);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Fetch clients for selection
  useEffect(() => {
    if (isOpen && !initialClientId) {
      const fetchClients = async () => {
        try {
          setLoadingClients(true);
          const response = await clientService.fetchClients(0, 100);
          setClients(response.clients || []);
        } catch (err) {
          console.error('Failed to fetch clients:', err);
        } finally {
          setLoadingClients(false);
        }
      };
      fetchClients();
    }
  }, [isOpen, initialClientId]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedDocTypes(['general']);
      setMessageToClient('');
      setMaxUploads(5);
      setExpiresInDays(7);
      setSendEmail(!!clientEmail);
      setError(null);
      setGeneratedUrl(null);
      setCopied(false);
      setSelectedClient(null);
      setClientSearch('');
    }
  }, [isOpen]);

  // Update sendEmail when client changes
  useEffect(() => {
    setSendEmail(!!clientEmail);
  }, [clientEmail]);

  // Filter clients by search
  const filteredClients = clients.filter((c) =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.email?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  // Toggle document type selection
  const toggleDocType = (docType: DocType) => {
    setSelectedDocTypes((prev) => {
      if (prev.includes(docType)) {
        // Don't allow deselecting if it's the last one
        if (prev.length === 1) return prev;
        return prev.filter((t) => t !== docType);
      }
      return [...prev, docType];
    });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      setIsLoading(true);

      const request: CreateSecureUploadLinkRequest = {
        client_id: clientId,
        document_types: selectedDocTypes,
        max_uploads: maxUploads,
        expires_in_days: expiresInDays,
        send_email: sendEmail,
      };

      if (messageToClient.trim()) {
        request.message_to_client = messageToClient.trim();
      }

      const response = await secureUploadLinkService.createLink(request);
      setGeneratedUrl(response.portal_url);
      onSuccess?.(response.portal_url);
    } catch (err) {
      console.error('Failed to create upload link:', err);
      setError(err instanceof Error ? err.message : 'Failed to create upload link');
    } finally {
      setIsLoading(false);
    }
  };

  // Copy URL to clipboard
  const handleCopyUrl = useCallback(async () => {
    if (!generatedUrl) return;

    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [generatedUrl]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-xl w-full max-w-lg transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Link2 size={20} className="text-[#002B45]" />
              <h2 className="text-lg font-semibold text-gray-900">
                Generate Secure Upload Link
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isLoading}
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          {generatedUrl ? (
            // Success state - show generated link
            <div className="px-6 py-6">
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Upload Link Created
                </h3>
                <p className="text-sm text-gray-600">
                  {sendEmail
                    ? `An email has been sent to ${clientEmail || 'the client'}.`
                    : 'Share this link with your client to request documents.'}
                </p>
              </div>

              {/* Link display and copy */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Portal URL
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={generatedUrl}
                    readOnly
                    className="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCopyUrl}
                    className="flex items-center gap-1.5"
                  >
                    {copied ? (
                      <>
                        <Check size={16} />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy size={16} />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Info note */}
              <div className="flex items-start gap-2 text-sm text-gray-600 bg-blue-50 rounded-lg p-3">
                <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <p>
                  The client will need to verify their identity using a one-time
                  code sent to their email before they can upload documents.
                </p>
              </div>

              {/* Close button */}
              <div className="mt-6 flex justify-end">
                <Button onClick={onClose}>Done</Button>
              </div>
            </div>
          ) : (
            // Form state
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Client Selector (when no clientId prop) */}
                {!initialClientId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Client
                    </label>
                    {selectedClient ? (
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">
                            {selectedClient.name}
                          </span>
                          {selectedClient.email && (
                            <span className="text-sm text-gray-500">
                              ({selectedClient.email})
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedClient(null)}
                          className="text-sm text-[#002B45] hover:underline"
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-lg">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search clients..."
                            value={clientSearch}
                            onChange={(e) => setClientSearch(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 text-sm border-b border-gray-200 focus:outline-none focus:ring-0"
                          />
                        </div>
                        <div className="max-h-40 overflow-y-auto">
                          {loadingClients ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                            </div>
                          ) : filteredClients.length === 0 ? (
                            <div className="text-center py-4 text-sm text-gray-500">
                              No clients found
                            </div>
                          ) : (
                            filteredClients.map((client) => (
                              <button
                                key={client.id}
                                type="button"
                                onClick={() => setSelectedClient(client)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                              >
                                <User size={14} className="text-gray-400" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {client.name}
                                  </div>
                                  {client.email && (
                                    <div className="text-xs text-gray-500">{client.email}</div>
                                  )}
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Client Info (when clientId prop provided) */}
                {initialClientId && clientName && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Client:</span>
                    <span className="text-sm font-medium text-gray-900">{clientName}</span>
                    {clientEmail && (
                      <span className="text-sm text-gray-500">({clientEmail})</span>
                    )}
                  </div>
                )}

                {/* Document Types */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Requested Document Types
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_DOC_TYPES.map((docType) => (
                      <button
                        key={docType}
                        type="button"
                        onClick={() => toggleDocType(docType)}
                        className={`
                          flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm
                          transition-colors
                          ${
                            selectedDocTypes.includes(docType)
                              ? 'border-[#002B45] bg-[#002B45]/5 text-[#002B45]'
                              : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }
                        `}
                      >
                        <FileText size={14} />
                        {getDocTypeLabel(docType)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Max Uploads and Expiration */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Uploads
                    </label>
                    <select
                      value={maxUploads}
                      onChange={(e) => setMaxUploads(Number(e.target.value))}
                      className="
                        w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                        focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                      "
                    >
                      {[1, 3, 5, 10, 20].map((n) => (
                        <option key={n} value={n}>
                          {n} {n === 1 ? 'file' : 'files'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Link Expires In
                    </label>
                    <select
                      value={expiresInDays}
                      onChange={(e) => setExpiresInDays(Number(e.target.value))}
                      className="
                        w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                        focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                      "
                    >
                      {EXPIRATION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Message to Client */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message to Client <span className="text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    value={messageToClient}
                    onChange={(e) => setMessageToClient(e.target.value)}
                    placeholder="Add instructions or context for the client..."
                    rows={3}
                    maxLength={1000}
                    className="
                      w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none
                      focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                    "
                  />
                </div>

                {/* Send Email Toggle */}
                {clientEmail && (
                  <div className="border-t border-gray-100 pt-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                        className="
                          w-4 h-4 rounded border-gray-300 text-[#002B45]
                          focus:ring-[#F7B500] focus:ring-offset-0
                        "
                      />
                      <div className="flex items-center gap-2">
                        <Mail size={16} className="text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">
                          Send email notification to client
                        </span>
                      </div>
                    </label>
                    <p className="mt-1 ml-7 text-xs text-gray-500">
                      Client will receive an email with the upload link at {clientEmail}
                    </p>
                  </div>
                )}

                {/* Error display */}
                {error && (
                  <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || selectedDocTypes.length === 0 || !clientId}
                  className="flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Link2 size={16} />
                      Generate Link
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateUploadLinkModal;
